"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const processSteps = [
  {
    icon: "/home-v3/container-4.svg",
    iconClassName: "h-16 w-16",
    title: "Tell Us What You Need",
    description:
      "Tell us where your business stands and what you need help with.",
    contentClassName: "sm:pb-[37px]",
    descriptionClassName: "text-[#a0a3b2]",
  },
  {
    icon: "/home-v3/container-1.svg",
    iconClassName:
      "h-16 w-16 backdrop-blur-[2px] backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(2px)_brightness(100%)]",
    title: "We Explain the Next Steps",
    description:
      "We explain what needs to happen, what details are needed, and what comes next.",
    contentClassName: "sm:pb-9",
    titleClassName: "max-w-[250px]",
  },
  {
    icon: "/home-v3/container.svg",
    iconClassName: "h-16 w-16",
    title: "Our Team Gets to Work",
    description:
      "Once everything is clear, our team helps manage the setup, records, filings, and compliance work.",
    contentClassName: "sm:pb-3",
    titleClassName: "whitespace-nowrap",
  },
  {
    icon: "/home-v3/container-4.svg",
    iconClassName: "h-[66px] w-[66px]",
    title: "Stay Updated Along the Way",
    description:
      "You get updates along the way, so you know what is handled and what needs attention.",
    contentClassName: "sm:pb-3",
    titleClassName: "sm:max-w-[240px]",
    descriptionClassName: "max-w-[254px]",
  },
];

// Marker verticals carry a fixed-px value (tuned against the 499px-tall map
// box used up to xl) plus a percentage equivalent that takes over at 2xl,
// where the box height switches to the viewport-relative MAP_BOX_HEIGHT below.
// The percentages are the px values over 499, so both express the same spot.
const mapMarkers = [
  {
    left: "left-[50.92%]",
    top: "top-[51px] 2xl:top-[10.22%]",
    outerColor: "bg-[#0061fe4c]",
    middleColor: "bg-[#0061fe99]",
    innerColor: "bg-[#0061fe]",
  },
  {
    left: "left-[77.34%]",
    top: "top-[91px] 2xl:top-[18.24%]",
    outerColor: "bg-[#0061fe4c]",
    middleColor: "bg-[#0061fe99]",
    innerColor: "bg-[#0061fe]",
  },
  {
    left: "left-[72.16%]",
    top: "top-[216px] 2xl:top-[43.29%]",
    outerColor: "bg-[#0061fe4c]",
    middleColor: "bg-[#0061fe99]",
    innerColor: "bg-[#0061fe]",
  },
  {
    left: "left-[56.18%]",
    top: "top-[260px] 2xl:top-[52.1%]",
    outerColor: "bg-[#0061fe4c]",
    middleColor: "bg-[#0061fe99]",
    innerColor: "bg-[#0061fe]",
  },
  {
    left: "left-[41.55%]",
    top: "top-48 2xl:top-[38.48%]",
    outerColor: "bg-[#0061fe4c]",
    middleColor: "bg-[#0061fe99]",
    innerColor: "bg-[#0061fe]",
  },
  {
    left: "left-[26.78%]",
    top: "top-[101px] 2xl:top-[20.24%]",
    outerColor: "bg-[#28b4464c]",
    middleColor: "bg-[#28b44699]",
    innerColor: "bg-[#17b26a]",
  },
];

// map.png is 1080x408 (aspect 2.65) but is rendered object-cover into a box
// that is 1440x500 (aspect 2.88) on a 1440px screen — a small, deliberate
// vertical crop. Past 1440 a fixed 500px height would keep widening the box
// and crop harder and harder, zooming the map and pulling the markers off
// their countries. Tying the height to the viewport (100vw / 2.88) keeps the
// box aspect — and therefore the crop and the marker alignment — identical at
// every width from 2xl up. Applied to the box and the image together.
const MAP_BOX_HEIGHT = "2xl:h-[34.72vw]";

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

export const EssentialBusinessServicesSection = (): JSX.Element => {
  return (
    <section
      aria-labelledby="how-it-works-heading"
      className="flex w-full flex-col mt-20 sm:mt-32 items-center max-sm:gap-[43px] gap-[83px] px-4 py-0"
    >
      <header className="flex w-full flex-col items-center gap-6 pt-6">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(3.5px)_brightness(100%)]">
          How It Works
        </Badge>
        <motion.h2
          id="how-it-works-heading"
          className="w-full [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-4xl font-normal leading-[1.2] tracking-[-1.92px] text-white sm:text-5xl sm:leading-[68px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          A simple way to work with us
        </motion.h2>
      </header>
      <div className="flex w-full max-w-[1408px] flex-col items-center gap-6">
        <div
          aria-label="Global business network"
          className={`relative hidden h-[300px] w-full sm:block sm:h-[400px] lg:h-[499px] ${MAP_BOX_HEIGHT}`}
        >
          <img
            className={`absolute max-sm:hidden opacity-50 left-1/2 top-0 h-[500px] w-screen max-w-none -translate-x-1/2 object-cover ${MAP_BOX_HEIGHT}`}
            alt=""
            aria-hidden="true"
            src="/home-v3/map.png"
          />

          {mapMarkers.map((marker, index) => (
            <span
              key={`map-marker-${index}`}
              className={`absolute hidden h-10 w-10 rounded-full sm:block 2xl:h-11 2xl:w-11 3xl:h-12 3xl:w-12 ${marker.left} ${marker.top} ${marker.outerColor}`}
              style={{ animationDelay: `${index * 0.35}s` }}
            >
              <span
                className={`absolute inset-0 rounded-full motion-safe:animate-marker-ping ${marker.outerColor}`}
                style={{ animationDelay: `${index * 0.35}s` }}
              />
              <span
                className={`absolute left-[19.84%] top-[19.84%] h-[60.32%] w-[60.32%] rounded-full ${marker.middleColor}`}
              />
              <span
                className={`absolute left-[26.19%] top-[26.19%] h-[47.61%] w-[47.61%] rounded-full motion-safe:animate-heartbeat ${marker.innerColor}`}
                style={{ animationDelay: `${index * 0.35}s` }}
              />
            </span>
          ))}

          <aside className="absolute left-[16.62%] top-[39px] hidden w-[154px] rounded-[20px_20px_0px_20px] bg-[#0f1319] px-2.5 pb-2.5 pt-2.5 sm:block 2xl:top-[7.82%] 2xl:w-[178px] 2xl:px-3 2xl:py-3 3xl:w-[196px]">
            <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-lg font-normal leading-[18px] tracking-[-0.24px] text-white 2xl:text-xl 2xl:leading-5 3xl:text-[22px] 3xl:leading-6">
              Hong Kong
            </p>
            <p className="mt-2 [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-4 tracking-[-0.48px] text-[#a0a4b3] 2xl:text-lg 2xl:leading-[18px] 3xl:text-xl 3xl:leading-5">
              Business Hub
            </p>
          </aside>
        </div>
        <motion.div
          className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
          variants={cardsContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          {processSteps.map((step) => (
            <motion.div key={step.title} variants={cardVariants}>
              <Card className="relative min-h-[300px] sm:min-h-[340px] overflow-hidden rounded-[32px] border-0 bg-[#0f1319] shadow-none">
                <span className="absolute -right-[76px] -top-[49px] h-[171px] w-[171px] rounded-full bg-[#6fa6ff] blur-[71.73px]" />
                <span className="absolute bottom-[-94px] left-[9%] h-72 w-[91%] rounded-[50%] bg-[#0061fe] blur-[75px]" />
                <CardContent
                  className={`relative flex h-full flex-col items-start gap-6 px-6 py-5 sm:px-10 sm:pt-10 ${step.contentClassName}`}
                >
                  <img
                    className={step.iconClassName}
                    alt=""
                    aria-hidden="true"
                    src={step.icon}
                  />
                  <div className="flex flex-col items-start gap-4">
                    <h3
                      className={`[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-xl sm:text-2xl font-normal leading-[28.8px] tracking-[-0.24px] text-white ${step.titleClassName ?? ""}`}
                    >
                      {step.title}
                    </h3>
                    <p
                      className={`[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-[22.4px] tracking-[-0.16px] text-[#a0a4b3] ${step.descriptionClassName ?? ""}`}
                    >
                      {step.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
